/**
 * objectDrag — drag-to-move / drag-corner-to-resize for floating sheet
 * objects (charts AND images) while the reader is in Edit mode.
 *
 * The adjusted geometry lives in VisualOverlay (keyed per object), not in
 * each pane copy, so every freeze-pane copy of an object moves together and
 * the selection / copy logic can read the object's current size.
 *
 * Mouse deltas are SCREEN px; the overlay renders under a CSS `zoom`, so
 * they are converted via the element's client-rect / layout-size ratio.
 *
 * @param {MouseEvent} e        the mousedown event
 * @param {"move"|"resize"} mode
 * @param {HTMLElement} el      the object's root element (for zoom ratio)
 * @param {{dx,dy,w,h}} start   geometry at drag start (content px)
 * @param {(g:{dx,dy,w,h}) => void} onChange
 * @param {() => void} [onEnd]
 * @param {{minW?:number, minH?:number}} [limits]
 */
export function startObjectDrag(e, mode, el, start, onChange, onEnd, limits = {}) {
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  const effZoom = el && el.offsetWidth > 0
    ? el.getBoundingClientRect().width / el.offsetWidth
    : 1;
  const minW = limits.minW != null ? limits.minW : 40;
  const minH = limits.minH != null ? limits.minH : 30;
  const x0 = e.clientX;
  const y0 = e.clientY;
  const onMove = (ev) => {
    const mx = (ev.clientX - x0) / effZoom;
    const my = (ev.clientY - y0) / effZoom;
    if (mode === "move") onChange({ ...start, dx: start.dx + mx, dy: start.dy + my });
    else onChange({ ...start, w: Math.max(minW, start.w + mx), h: Math.max(minH, start.h + my) });
  };
  const onUp = () => {
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
    if (typeof onEnd === "function") onEnd();
  };
  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
}

/**
 * Green frame around the SELECTED chart/image — drawn inside the object's
 * box so a freeze-pane clip-path can't cut it off.
 */
export const SELECTION_FRAME_STYLE = {
  position: "absolute",
  inset: 0,
  border: "2px solid #217346",
  boxSizing: "border-box",
  pointerEvents: "none",
  zIndex: 2,
};

/** Corner resize-handle style shared by charts and images (inside the box). */
export const RESIZE_HANDLE_STYLE = {
  position: "absolute",
  right: 0,
  bottom: 0,
  zIndex: 3,
  width: 14,
  height: 14,
  cursor: "nwse-resize",
  borderRight: "3px solid #217346",
  borderBottom: "3px solid #217346",
  borderBottomRightRadius: 4,
  opacity: 0.85,
};
